import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { PageHero, Reveal, Label, CTASection } from "../components/Shared";
import Ribbon from "../components/Ribbon";
import { PROCESS, IMAGES } from "../data/site";

const PROVIDE = [
  "Cleanroom manufacturing under ISO 7 certification",
  "Custom formulation with client-specified ingredients",
  "Complete documentation package (CoA, SDS, ISO cert)",
  "Finished, labeled product ready for market",
  "Scalable batch sizes as your brand grows",
];

const IDEAL = [
  { title: "Wellness Brands", body: "Entrepreneurs ready to launch a branded suppository line." },
  { title: "Healthcare Companies", body: "Organizations extending a compliant product catalog." },
  { title: "Physicians", body: "Providers offering custom branded products to patients." },
];

const Timeline = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start center", "end center"],
  });
  const height = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section ref={ref} className="bg-navy py-24 lg:py-32" data-testid="process-timeline">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <Reveal>
          <Label light>The Process</Label>
          <h2 className="mt-6 max-w-2xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
            From inquiry to delivery.
          </h2>
        </Reveal>

        <div className="relative mt-20 pl-10 lg:pl-16">
          {/* track */}
          <div className="absolute left-[7px] top-0 h-full w-px bg-white/15 lg:left-[11px]" />
          <motion.div
            style={{ height }}
            className="absolute left-[7px] top-0 w-px bg-clinical lg:left-[11px]"
          />

          <div className="space-y-16 lg:space-y-24">
            {PROCESS.map((p, i) => (
              <Reveal key={p.no} delay={i * 0.05}>
                <div className="relative">
                  <span className="absolute -left-10 top-1.5 flex h-4 w-4 items-center justify-center lg:-left-16">
                    <span className="h-4 w-4 rounded-full border-2 border-clinical bg-navy" />
                  </span>
                  <div className="grid gap-4 lg:grid-cols-12 lg:items-baseline">
                    <span className="font-display text-5xl font-extrabold text-white/15 lg:col-span-2 lg:text-6xl">
                      {p.no}
                    </span>
                    <h3 className="font-heading text-2xl font-bold text-white lg:col-span-3 sm:text-3xl">
                      {p.title}
                    </h3>
                    <p className="text-lg leading-relaxed text-slate-300 lg:col-span-7">
                      {p.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default function PrivateLabel() {
  return (
    <>
      <PageHero
        label="Private Label"
        titleLines={["Your brand.", "Our certified", "facility."]}
        intro="Private label lets your brand offer finished, labeled suppository products — without operating a facility. We manufacture; you own the brand."
        image={IMAGES.labTube}
        imageAlt="Precision formulation work"
      />
      <Ribbon />

      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
            <Reveal>
              <Label>What Private Label Means</Label>
              <h2 className="mt-6 font-heading text-4xl font-extrabold leading-[1.1] tracking-tight text-navy sm:text-5xl">
                We build it. You brand it.
              </h2>
              <p className="mt-8 text-lg leading-relaxed text-slate-600">
                Private label manufacturing means MD Labs produces your product to
                specification while you retain full ownership of the brand. It's the
                fastest, most credible route to bringing a compliant suppository
                product to market under your own name.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="border border-slate-200 p-10">
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-clinical">
                  What We Provide
                </span>
                <ul className="mt-8 space-y-5">
                  {PROVIDE.map((item) => (
                    <li key={item} className="flex items-start gap-4">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center bg-navy">
                        <Check className="h-4 w-4 text-white" />
                      </span>
                      <span className="text-[15px] font-medium leading-relaxed text-navy">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-[#FAFAFA] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <Reveal>
            <Label>Ideal Partners</Label>
            <h2 className="mt-6 font-heading text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
              Who private label is built for.
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {IDEAL.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <div className="group h-full border border-slate-200 bg-white p-10 transition-colors duration-300 hover:border-navy">
                  <div className="font-display text-2xl font-extrabold text-clinical">
                    0{i + 1}
                  </div>
                  <h3 className="mt-8 font-heading text-2xl font-bold text-navy">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-slate-500">
                    {p.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Timeline />
      <CTASection />
    </>
  );
}
